import { SpartenType } from '@ps20/bff-models';
import { FeatureToggles } from '@ps20-frontend/frontend-models';
import {
  ProductUw,
  UwContextDescriptor,
  UwContextEntry,
  UwContextKey,
  UwEvalContext,
} from './uw-context.model';

export function selectUwEntries(
  uw: ProductUw | undefined,
  key?: UwContextKey,
): UwContextEntry[] {
  const entries = uw?.entries ?? [];
  return key === undefined ? entries : entries.filter(entry => entry.contextKey === key);
}

export function isUwEntryActive(
  entry: UwContextEntry,
  descriptors: UwContextDescriptor[],
  featureToggles: FeatureToggles | undefined,
): boolean {
  if (!entry.isRequired) {
    return false;
  }
  const descriptor = findDescriptor(entry, descriptors);
  if (!descriptor) {
    return false;
  }
  return !descriptor.enabledWhen || descriptor.enabledWhen(featureToggles ?? ({} as FeatureToggles));
}

function findDescriptor(
  entry: UwContextEntry,
  descriptors: UwContextDescriptor[],
): UwContextDescriptor | undefined {
  return descriptors.find(candidate => candidate.key === entry.contextKey);
}

function isDescriptorDisabled(
  descriptor: UwContextDescriptor,
  featureToggles: FeatureToggles | undefined,
): boolean {
  return Boolean(descriptor.enabledWhen) && !descriptor.enabledWhen(featureToggles ?? ({} as FeatureToggles));
}

function findDescriptorOrdinal(
  entry: UwContextEntry,
  descriptors: UwContextDescriptor[],
): number {
  return descriptors.find(descriptor => descriptor.key === entry.contextKey)?.ordinal ?? Number.POSITIVE_INFINITY;
}

export function selectActiveUwEntries(
  uw: ProductUw | undefined,
  descriptors: UwContextDescriptor[],
  featureToggles: FeatureToggles | undefined,
): UwContextEntry[] {
  return selectUwEntries(uw)
    .filter(entry => isUwEntryActive(entry, descriptors, featureToggles))
    .sort(
      (left, right) =>
        findDescriptorOrdinal(left, descriptors) - findDescriptorOrdinal(right, descriptors),
    );
}

export function selectRenderableUwEntries(
  uw: ProductUw | undefined,
  descriptors: UwContextDescriptor[],
  featureToggles: FeatureToggles | undefined,
): UwContextEntry[] {
  return selectActiveUwEntries(uw, descriptors, featureToggles).slice(0, 1);
}

export function selectEnabledUwContexts(
  descriptors: UwContextDescriptor[],
  featureToggles: FeatureToggles | undefined,
): UwContextDescriptor[] {
  return descriptors.filter(
    descriptor =>
      !descriptor.enabledWhen || descriptor.enabledWhen(featureToggles ?? ({} as FeatureToggles)),
  );
}

export function doesUwContextApplyToSparte(
  descriptor: UwContextDescriptor,
  spartenType: SpartenType,
): boolean {
  return !descriptor.appliesTo || descriptor.appliesTo(spartenType);
}

export function selectUwContextsForSparte(
  descriptors: UwContextDescriptor[],
  spartenType: SpartenType,
): UwContextDescriptor[] {
  return descriptors.filter(descriptor => doesUwContextApplyToSparte(descriptor, spartenType));
}



function isEntryDocumentationComplete(
  entry: UwContextEntry,
  descriptor: UwContextDescriptor,
): boolean {
  const hasFiles = entry.files.length > 0;
  const hasRequiredTexts = descriptor.textFields
    .filter(textField => textField.required)
    .every(textField => (entry.texts[textField.id]?.trim().length ?? 0) > 0);
  return hasFiles && hasRequiredTexts;
}

export function selectIsUwComplete(
  uw: ProductUw | undefined,
  descriptors: UwContextDescriptor[],
  ctx: UwEvalContext,
): boolean {
  return selectUwEntries(uw)
    .filter(entry => entry.isRequired)
    .every(entry => {
      const descriptor = findDescriptor(entry, descriptors);
      if (!descriptor) {
        return false;
      }
      if (isDescriptorDisabled(descriptor, ctx.featureToggles)) {
        return true;
      }
      return isEntryDocumentationComplete(entry, descriptor);
    });
}
